import type { VulnComment } from '../../../modules/types/vulnerabilities'

/** A deleted top-level comment that must still render (because it has replies). */
export function isTombstone(c: VulnComment): boolean {
  return c.deleted && (c.replies?.length ?? 0) > 0
}

/**
 * Defensive client-side filter matching the comments contract. The backend already
 * omits fully-deleted rows, but we re-apply the rule so the UI is correct regardless.
 */
export function visibleComments(comments: VulnComment[]): VulnComment[] {
  return comments
    .filter((c) => !c.deleted || isTombstone(c))
    .map((c) => ({ ...c, replies: (c.replies ?? []).filter((r) => !r.deleted) }))
}

/**
 * Flattens the pages of the comments infinite query into one list, keeping API
 * order and dropping any id already seen. A comment inserted while the reader
 * is paging shifts the page window, so the same row can legitimately arrive on
 * two consecutive pages; the first copy wins.
 */
export function mergeCommentPages(pages: VulnComment[][] = []): VulnComment[] {
  const seen = new Set<string>()
  const merged: VulnComment[] = []
  for (const comment of pages.flat()) {
    if (!comment || seen.has(comment.id)) continue
    seen.add(comment.id)
    merged.push(comment)
  }
  return merged
}
